import React, { useEffect, useState } from 'react';
import { Navigate } from 'react-router-dom';
import axios from 'axios';
import { useAuth } from '../Login/AuthContext';
import UserDashboard from './UserDashboard';
import ArtistDashboard from './ArtistDashboard';
import VenueOwnerDashboard from './VenueOwnerDashboard';
import AdminDashboard from './AdminDashboard';

// API URL from environment
const API_URL = process.env.REACT_APP_API_URL;

const Dashboard = ({ onBack }) => {
  const { currentUser, logout: authLogout } = useAuth();
  const [user, setUser] = useState(null);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState(null);

  useEffect(() => {
    const fetchUserData = async () => {
      try {
        // Try to get user from localStorage first
        const storedUser = localStorage.getItem('user');
        if (storedUser) {
          const parsedUser = JSON.parse(storedUser);
          console.log('[Dashboard] Using user from localStorage');
          setUser(parsedUser);
        }

        // In either case, verify with the server
        await fetchFromServer();

      } catch (err) {
        handleError(err);
      } finally {
        setLoading(false);
      }
    };

    const fetchFromServer = async () => {
      try {
        // Get token from localStorage
        const token = localStorage.getItem('token');
        console.log('[Dashboard] Token from localStorage →', token ? 'Found' : 'Not found');

        if (!token) {
          throw new Error('Missing token');
        }

        // Call the API with correct endpoint
        const response = await axios.get(`${API_URL}/user`, {
          headers: { Authorization: `Bearer ${token}` },
          withCredentials: true // For cookie-based auth if needed
        });

        console.log('[Dashboard] /api/user response →', response.data);

        // Set user from response
        const userData = response.data;
        setUser(userData);
        
        // Update localStorage with fresh data
        localStorage.setItem('user', JSON.stringify(userData));
      } catch (err) {
        handleError(err);
      }
    };

    const handleError = async (err) => {
      console.error(
        '[Dashboard] Error fetching user:',
        err.response?.status,
        err.response?.data || err.message
      );

      setError(err);

      // Handle 401 (Unauthorized) - token might be expired or invalid
      if (err.response?.status === 401) {
        console.info('[Dashboard] 401 Unauthorized - clearing auth state');
        try {
          // Use AuthContext's logout to properly clear all auth state
          await authLogout();
        } catch (logoutError) {
          console.error('[Dashboard] Error during logout:', logoutError);
        }
      }
    };

    fetchUserData();
  }, []);

  // If still loading, show loading spinner
  if (loading) {
    return (
      <div className="loading-container">
        <div className="loading-spinner"></div>
        <p>กำลังโหลด...</p>
      </div>
    );
  }

  // If error or no user, redirect to login
  if (error || !user) {
    console.log('[Dashboard] Error or no user, redirecting to /login');
    return <Navigate to="/login" replace />;
  }

  // Select the appropriate dashboard based on user role
  console.log('[Dashboard] Rendering dashboard for role:', user.role);
  
  switch (user.role) {
    case 'admin':
      return <AdminDashboard user={user} />;
    case 'artist':
      return <ArtistDashboard user={user} />;
    case 'venue_owner':
      return <UserDashboard user={user} onBack={onBack} />;
    default:
      return <UserDashboard user={user} onBack={onBack} />;
  }
};

export default Dashboard;